// @LTG-ID: data/journals/what-is-a-valuation-multiple
// @STATUS: BUILT
// @HERMETIC: true
// @DOCS: /app/docs/data-model/journal_content_v1.md, /app/docs/09_CHANGELOG.md#CHG-0107
// @CHANGED: 2026-07-06 (CHG-0107 · Journals content port from batch MD)
// @OWNER: web
//
// Article 5 of 20. Source: batch MD at /app/_ltg_analysis/content_batches/.
// Byline: LTG Team (per CHG-0107 orchestrator Decision 2 / batch preamble directive).

export const BRIEF = {
    SLUG: "what-is-a-valuation-multiple",
    FRONTMATTER: {
        number: "42",
        section: "Valuation",
        sectionHref: "/journals#core-valuation",
        sectionAnchor: "core-valuation",
        title: "What Is a Valuation Multiple and Why Does Everyone Talk About Them?",
        deck: "A valuation multiple is the number applied to a business's earnings to estimate its value.",
        author: "LTG Team",
        authorRole: "",
        authorInitials: "LT",
        publishedISO: "2026-05-22",
        publishedHuman: "May 22, 2026",
        lastReviewed: "2026-08-27",
        readMinutes: 3,
        wordCount: 318,
        timeRequired: "PT3M",
        keywords: "what is a valuation multiple, business valuation multiples, EBITDA multiple meaning, SDE multiple",
        articleSection: "Valuation",
        articleBodyPreview: "A valuation multiple is the number applied to a business's earnings to estimate its value. If a business earns $500,000 and sells for $1.5 million, it sold at a 3x multiple. Multiples come from actual market transactions and reflect how risky and durable buyers believe a business's earnings are. A multiple is the market's shorthand for risk and durability. When a business sells for three times earnings rather than two, buyers are saying they believe those earnings are safer, more transferable, a",
        complianceNote: "This article is educational and does not constitute financial, legal, or tax advice.",
        needsDisclaimerFooter: false,
        internalLinks: [
        "how-much-is-my-business-worth",
        "what-is-ebitda",
        "sde-vs-ebitda-guide"
    ],
        ctaId: "journal-article-primary",
        ctaLabel: "Get Your Valuation",
        pageTitle: "What Is a Valuation Multiple? A Plain-English Guide · LTG",
        canonical: "https://letstalkgrowth.com/journals/what-is-a-valuation-multiple",
        ogImage: "/journals/og/what-is-a-valuation-multiple.jpg",
    },
    BODY_MARKDOWN: `A valuation multiple is the number applied to a business's earnings to estimate its value. If a business earns $500,000 and sells for $1.5 million, it sold at a 3x multiple. Multiples come from actual market transactions and reflect how risky and durable buyers believe a business's earnings are.

A multiple is the market's shorthand for risk and durability. When a business sells for three times earnings rather than two, buyers are saying they believe those earnings are safer, more transferable, and more likely to grow.

## Where multiples come from

Multiples are not invented; they are observed. Databases of completed transactions, along with published research like BizBuySell's Insight Reports and the IBBA Market Pulse, record what real buyers paid for real businesses relative to their earnings. Advisors then look at sales of comparable businesses to frame what a similar company might command.

## What moves a multiple

Within any industry, multiples occupy a range, and where a specific business falls depends on characteristics buyers reward or punish:

- Recurring or contracted revenue pushes multiples up; purely project-based revenue pulls them down.
- A business that runs without its owner earns a premium; heavy owner dependence earns a discount.
- Diverse customers support the top of the range; concentration drags toward the bottom, or below it.
- Clean financial statements support confidence; messy books create discounts that dwarf the cost of good bookkeeping.
- Size itself matters: larger earnings streams attract more buyers and financing, which lifts multiples.

## One number, easily misused

A multiple only means something when paired with the right earnings measure. An SDE multiple applied to EBITDA, or vice versa, produces a wrong answer with confident-looking math. And an industry's average multiple describes the average business, which yours is not. The useful question is never "what is the multiple for my industry" but "what about my business argues for the top of the range, and what argues for the bottom?"`,
    FAQ: [
        { question: "Are valuation multiples applied to revenue or profit?", answer: "Almost always to profit, meaning SDE or EBITDA, for private businesses. Revenue multiples are common only in a handful of sectors with unusual economics." },
        { question: "Why do larger businesses get higher multiples?", answer: "Larger earnings streams are seen as more durable, attract more buyer competition, and are easier to finance, all of which push multiples upward." },
        { question: "Is a rule-of-thumb multiple from my industry association reliable?", answer: "It is a starting point, not an answer. Rules of thumb average together strong and weak businesses. Your specific risk profile determines where in the range, or outside it, your business belongs." }
    ],
    SOURCES: [

    ],
    END_CTA: {
        label: "READY TO KNOW YOUR NUMBER?",
        headlineLead: "What is your business ",
        headlineEm: "actually",
        headlineTrail: " worth?",
        body: "Indicative valuation signed by Sara. $995 flat.",
        secondaryHref: "/journals",
        secondaryLabel: "More from the Journal →",
        microLine: "· 7-DAY DELIVERY · KGOB METHODOLOGY, NC CPA #30420 ·",
    },
    READ_NEXT: [
        {
            tag: "Valuation",
            title: "How Much Is My Business Worth? The Owner's Complete Guide to Business Valuation",
            meta: "5 min · Brief 38",
            to: "/journals/how-much-is-my-business-worth",
        },
        {
            tag: "Valuation",
            title: "What Is EBITDA? A Plain-English Guide for Business Owners",
            meta: "3 min · Brief 40",
            to: "/journals/what-is-ebitda",
        },
        {
            tag: "Valuation",
            title: "SDE vs EBITDA: Which One Applies to Your Business?",
            meta: "3 min · Brief 41",
            to: "/journals/sde-vs-ebitda-guide",
        },
    ],
};

export default BRIEF;
